import { FC, Fragment, useState } from 'react'
import { DialogTitle, DialogActions, DialogContent, DialogContentText, Button, Box, Autocomplete, TextField, CircularProgress } from '@mui/material'
import { ServicePlan } from 'src/api'
import { StatefulDialog } from 'src/components'
import { useSensorServicePlansRequest } from 'src/hooks/api'
import { DialogNames } from 'src/enums'

interface ActivateSensorInThingspaceModalProps {
  imei: string
  isLoading?: boolean
  onSubmit: (servicePlan: string) => void
  onCancel: () => void
}

const getServicePlanLabel = (plan: ServicePlan): string => [plan.name, plan.code].filter(Boolean).join(', ')

const ActivateSensorInThingspaceDialog: FC<ActivateSensorInThingspaceModalProps> = ({ imei, isLoading, onSubmit, onCancel }) => {
  const { isInitialLoading, isError, data: servicePlans } = useSensorServicePlansRequest()
  const [selectedPlan, setSelectedPlan] = useState<ServicePlan | null>(null)

  return (
    <>
      <DialogTitle>Request ThingSpace sensor activation</DialogTitle>
      <DialogContent>
        <DialogContentText>Please choose the service plan for the device</DialogContentText>
        <Box width="400px" maxWidth="100%" py={2}>
          <Autocomplete
            id="service-plans"
            options={servicePlans || []}
            getOptionLabel={getServicePlanLabel}
            style={{ width: '100%' }}
            loading={isInitialLoading}
            disabled={isInitialLoading || isError}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Service plan"
                placeholder="Service plan..."
                error={isError}
                helperText={isError ? 'Failed to load service plans' : undefined}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {isInitialLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  )
                }}
              />
            )}
            value={selectedPlan}
            onChange={(event, value) => setSelectedPlan(value)}
          />
        </Box>
        <DialogContentText>
          Are you sure you want to activate the GAT with IMEI <strong>{imei}</strong>?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!selectedPlan?.name || isLoading} onClick={() => onSubmit(selectedPlan?.name as string)}>
          {isLoading ? <CircularProgress size={22} /> : 'Activate'}
        </Button>
      </DialogActions>
    </>
  )
}

export const ActivateSensorInThingspaceModal: FC<ActivateSensorInThingspaceModalProps> = (props) => {
  return (
    <StatefulDialog name={DialogNames.ActivateSensorInThingspace} wrapContent={false}>
      <ActivateSensorInThingspaceDialog {...props} />
    </StatefulDialog>
  )
}
